import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiKey, setApiKey } from "../api";
import { Icon } from "../components/Icon";
import { LANGS, useLang, useT } from "../i18n";
import { useToast } from "../lib/toast";
import { ACCENTS, applyAccent, applyDensity } from "../theme";

const DENSITIES: [string, string][] = [["compact", "Compact"], ["regular", "Normal"], ["comfy", "Aéré"]];

export function SettingsPage() {
  const tr = useT();
  const nav = useNavigate();
  const toast = useToast((s) => s.show);
  const { lang, setLang } = useLang();
  const [accent, setAccentI] = useState(() => Number(localStorage.getItem("pf_accent") || 0));
  const [density, setDensity] = useState(() => localStorage.getItem("pf_density") || "regular");
  const [key, setKey] = useState(getApiKey());
  const [name, setName] = useState(() => localStorage.getItem("pf_name") || "");
  const [email, setEmail] = useState(() => localStorage.getItem("pf_email") || "");

  function pickAccent(i: number) { setAccentI(i); localStorage.setItem("pf_accent", String(i)); applyAccent(i); }
  function pickDensity(d: string) { setDensity(d); localStorage.setItem("pf_density", d); applyDensity(d); }
  function saveKey() { setApiKey(key.trim()); toast(tr(key.trim() ? "Clé API enregistrée" : "Clé API effacée"), "ok"); }
  function saveAccount() { localStorage.setItem("pf_name", name); localStorage.setItem("pf_email", email); toast(tr("Profil enregistré"), "ok"); }
  function signOut() { setApiKey(""); localStorage.removeItem("pf_name"); localStorage.removeItem("pf_email"); toast(tr("Déconnecté"), "ok"); nav("/"); }

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>{tr("Compte")}</div>
          <h1>{tr("Réglages")}</h1>
          <div className="sub">{tr("Apparence, accès et compte — synchronisés avec la barre latérale.")}</div>
        </div>
      </div>

      {/* Apparence */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 14 }}>{tr("Apparence")}</div>
        <div className="set-row">
          <div className="set-label"><b>{tr("Accent")}</b><span>{tr("La couleur primaire de l'interface.")}</span></div>
          <div className="row" style={{ gap: 8 }}>
            {ACCENTS.map((a, i) => (
              <button key={i} title={a[3]} onClick={() => pickAccent(i)} style={{
                width: 26, height: 26, borderRadius: 8, cursor: "pointer", padding: 0,
                background: `linear-gradient(135deg, ${a[0]}, ${a[1]})`,
                border: i === accent ? "2px solid #fff" : "1px solid var(--line-2)",
              }} />
            ))}
          </div>
        </div>
        <div className="set-row">
          <div className="set-label"><b>{tr("Densité")}</b><span>{tr("L'espacement des éléments.")}</span></div>
          <div className="seg-toggle">
            {DENSITIES.map(([v, l]) => <button key={v} className={density === v ? "on" : ""} onClick={() => pickDensity(v)}>{tr(l)}</button>)}
          </div>
        </div>
        <div className="set-row" style={{ borderBottom: "none" }}>
          <div className="set-label"><b>{tr("Langue")}</b><span>{tr("Langue du site vitrine.")}</span></div>
          <div className="seg-toggle">
            {LANGS.map((l) => <button key={l.id} className={lang === l.id ? "on" : ""} onClick={() => setLang(l.id)}>{l.flag}</button>)}
          </div>
        </div>
      </div>

      {/* Accès */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 6 }}>{tr("Accès API")}</div>
        <p className="muted" style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}>
          {tr("Les lectures publiques (galerie, previews) ne nécessitent rien. Les actions (créer, router, publier) et les données opérationnelles exigent ta clé")} <span className="kbd">x-api-key</span>.
        </p>
        <div className="row wrap" style={{ gap: 10, alignItems: "flex-end" }}>
          <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
            <label>{tr("Clé API")}</label>
            <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={tr("colle ta clé x-api-key…")} />
          </div>
          <button className="btn primary" onClick={saveKey}><Icon name="shield" size={14} /> {tr("Enregistrer")}</button>
          {key && <button className="btn ghost" onClick={() => { setKey(""); setApiKey(""); toast(tr("Clé effacée"), "ok"); }}>{tr("Effacer")}</button>}
        </div>
        <div className="muted row" style={{ gap: 6, fontSize: 11.5, marginTop: 10 }}>
          <Icon name={getApiKey() ? "check" : "alert"} size={12} /> {tr(getApiKey() ? "Clé définie — l'app est débloquée." : "Aucune clé — les actions resteront en lecture seule.")}
        </div>
      </div>

      {/* Compte */}
      <div className="card">
        <div className="card-title" style={{ marginBottom: 14 }}>{tr("Compte")}</div>
        <div className="row wrap" style={{ gap: 12 }}>
          <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}><label>{tr("Nom")}</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Ton nom")} /></div>
          <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}><label>E-mail</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="toi@exemple.com" /></div>
        </div>
        <div className="row between" style={{ marginTop: 16 }}>
          <button className="btn primary" onClick={saveAccount}><Icon name="check" size={14} /> {tr("Enregistrer")}</button>
          <button className="btn ghost" onClick={signOut}><Icon name="external" size={14} /> {tr("Se déconnecter")}</button>
        </div>
      </div>
    </div>
  );
}
